<template>
  <div class="tg-page-root tg-viewport-page min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-white to-purple-50 px-4 py-12 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
    <div class="w-full max-w-sm">
      <!-- Logo/Title Section -->
      <div class="text-center mb-10">
        <div class="flex justify-center mb-4">
          <img src="@/assets/img/logo.png" alt="LeSIM" class="h-16 w-auto drop-shadow-sm" />
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-300">{{ $t('Register_Welcome') }}</p>
      </div>

      <!-- Register Form -->
      <div class="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-white/60 dark:border-gray-700/60 shadow-xl shadow-violet-100/50 dark:shadow-none p-6 space-y-4">
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('Register_Email') }}
          </label>
          <div class="flex flex-col gap-2">
            <input
              v-model="email"
              type="email"
              :placeholder="$t('Register_Email_Placeholder')"
              class="w-full min-w-0 px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              :class="{ 'border-red-400 focus:ring-red-400 focus:border-red-400': emailError, 'bg-gray-50 dark:bg-gray-900': codeSent }"
              :disabled="codeSent"
            />
            <TurnstileWidget
              v-if="!codeSent"
              ref="sendCodeTurnstileRef"
              @success="onSendCodeTurnstileSuccess"
              @expired="onSendCodeTurnstileExpired"
            />
            <button
              v-if="!codeSent"
              type="button"
              @click="sendVerificationCode"
              :disabled="codeLoading || !email || sendCodeTurnstilePending"
              class="px-4 py-2.5 text-sm bg-brand text-white rounded-lg font-medium hover:bg-brand/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <span v-if="codeLoading">{{ $t('Register_Sending_Code') }}</span>
              <span v-else>{{ $t('Register_Send_Code') }}</span>
            </button>
          </div>
          <p v-if="emailError" class="text-red-500 text-xs mt-1">{{ emailError }}</p>
        </div>

        <div v-if="codeSent">
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('Register_Verification_Code') }}
          </label>
          <input
            v-model="verificationCode"
            type="text"
            :placeholder="$t('Register_Verification_Code_Placeholder')"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            :class="{ 'border-red-400 focus:ring-red-400 focus:border-red-400': codeError }"
            maxlength="6"
          />
          <p v-if="codeError" class="text-red-500 text-xs mt-1">{{ codeError }}</p>
          <p class="text-gray-500 dark:text-gray-300 text-xs mt-1">{{ $t('Register_Code_Sent_Message') }}</p>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('Register_Username') }} <span class="text-gray-400 dark:text-gray-300 font-normal">({{ $t('Register_Optional') }})</span>
          </label>
          <input
            v-model="username"
            type="text"
            :placeholder="$t('Register_Username_Placeholder')"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all"
            :class="{ 
              'border-red-400 focus:ring-red-400 focus:border-red-400': usernameError,
              'bg-gray-50 dark:bg-gray-900 cursor-not-allowed': !codeSent
            }"
            :disabled="!codeSent"
          />
          <p v-if="usernameError" class="text-red-500 text-xs mt-1">{{ usernameError }}</p>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('Register_Password') }}
          </label>
          <div class="relative">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('Register_Password_Placeholder')"
              class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all pr-10"
              :class="{ 
                'border-red-400 focus:ring-red-400 focus:border-red-400': passwordError,
                'bg-gray-50 dark:bg-gray-900 cursor-not-allowed': !codeSent
              }"
              :disabled="!codeSent"
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400 dark:text-gray-300 transition-colors"
              :disabled="!codeSent"
            >
              <svg v-if="showPassword" class="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
              <svg v-else class="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          </div>
          <p v-if="passwordError" class="text-red-500 text-xs mt-1">{{ passwordError }}</p>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('Register_Confirm_Password') }}
          </label>
          <input
            v-model="confirmPassword"
            :type="showPassword ? 'text' : 'password'"
            :placeholder="$t('Register_Confirm_Password_Placeholder')"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all"
            :class="{ 
              'border-red-400 focus:ring-red-400 focus:border-red-400': confirmPasswordError,
              'bg-gray-50 dark:bg-gray-900 cursor-not-allowed': !codeSent
            }"
            :disabled="!codeSent"
          />
          <p v-if="confirmPasswordError" class="text-red-500 text-xs mt-1">{{ confirmPasswordError }}</p>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('Register_Referrer_Code') }}
            <span v-if="!referrerFromUrlLocked" class="text-gray-400 dark:text-gray-300 font-normal">({{ $t('Register_Optional') }})</span>
          </label>
          <input
            v-model="referrerCode"
            type="text"
            :placeholder="$t('Register_Referrer_Code_Placeholder')"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all"
            :class="{ 'bg-gray-50 dark:bg-gray-900 cursor-not-allowed': !codeSent || referrerFromUrlLocked }"
            :disabled="!codeSent || referrerFromUrlLocked"
          />
        </div>

        <div class="flex items-start">
          <input
            v-model="receivePromotionalEmails"
            type="checkbox"
            class="w-3.5 h-3.5 rounded border-gray-300 text-brand focus:ring-1 focus:ring-brand mt-0.5 accent-brand"
            style="accent-color: #8B5CF6;"
            :disabled="!codeSent"
          />
          <label class="ml-2 text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
            {{ $t('Register_Receive_Promotional_Emails') }}
          </label>
        </div>

        <TurnstileWidget
          v-if="codeSent"
          ref="registerTurnstileRef"
          @success="onRegisterTurnstileSuccess"
          @expired="onRegisterTurnstileExpired"
        />

        <button
          type="button"
          @click="handleRegister"
          :disabled="loading || !codeSent || registerTurnstilePending"
          class="w-full bg-brand text-white py-2.5 rounded-lg font-medium text-sm hover:bg-brand/90 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="loading">{{ $t('Register_Loading') }}</span>
          <span v-else>{{ $t('Register_Button') }}</span>
        </button>

        <div v-if="codeSent" class="text-center text-xs text-gray-500 dark:text-gray-300 pt-2">
          {{ $t('Register_Or') }}
        </div>

        <button
          type="button"
          @click="goToLogin"
          class="w-full border border-brand text-brand py-2.5 rounded-lg font-medium text-sm hover:bg-brand/10 active:scale-[0.98] transition-all duration-200"
        >
          {{ $t('Register_Login_Link') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { showAppNotification } from '@/utils/appNotification'
import { inject } from 'vue'
import { setStorage } from '@/utils/storage'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import { getReferrerCodeFromQuery } from '@/utils/referrerFromQuery'
import logger from '@/utils/logger'
import { trackSignUp } from '@/tracking'
import TurnstileWidget from '@/components/TurnstileWidget.vue'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const api = inject('api')

const email = ref('')
const username = ref('')
const password = ref('')
const confirmPassword = ref('')
const verificationCode = ref('')
const referrerCode = ref('')
/** 来自 URL 的邀请码：注册全程只读 */
const referrerFromUrlLocked = ref(false)
const receivePromotionalEmails = ref(false)
const showPassword = ref(false)
const codeSent = ref(false)
const loading = ref(false)
const codeLoading = ref(false)

const emailError = ref('')
const usernameError = ref('')
const passwordError = ref('')
const confirmPasswordError = ref('')
const codeError = ref('')

const sendCodeTurnstileRef = ref(null)
const registerTurnstileRef = ref(null)
const sendCodeTurnstileToken = ref('')
const registerTurnstileToken = ref('')
const turnstileEnabled = !!process.env.VUE_APP_TURNSTILE_SITE_KEY
const sendCodeTurnstilePending = ref(turnstileEnabled)
const registerTurnstilePending = ref(turnstileEnabled)

const onSendCodeTurnstileSuccess = (token) => {
  sendCodeTurnstileToken.value = token
  sendCodeTurnstilePending.value = false
}

const onSendCodeTurnstileExpired = () => {
  sendCodeTurnstileToken.value = ''
  sendCodeTurnstilePending.value = true
}

const onRegisterTurnstileSuccess = (token) => {
  registerTurnstileToken.value = token
  registerTurnstilePending.value = false
}

const onRegisterTurnstileExpired = () => {
  registerTurnstileToken.value = ''
  registerTurnstilePending.value = true
}

onMounted(() => {
  const code = getReferrerCodeFromQuery(route.query)
  if (code) {
    referrerCode.value = code
    referrerFromUrlLocked.value = true
  }
})

const validateEmail = () => {
  emailError.value = ''
  if (!email.value) {
    emailError.value = t('Register_Email_Required')
    return false
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email.value)) {
    emailError.value = t('Register_Email_Invalid')
    return false
  }
  return true
}

const validateRegisterForm = () => {
  usernameError.value = ''
  passwordError.value = ''
  confirmPasswordError.value = ''
  codeError.value = ''


  if (!verificationCode.value) {
    codeError.value = t('Register_Verification_Code_Required')
    return false
  }

  if (!password.value) {
    passwordError.value = t('Register_Password_Required')
    return false
  }

  if (password.value.length < 6) {
    passwordError.value = t('Register_Password_Min_Length')
    return false
  }

  if (password.value !== confirmPassword.value) {
    confirmPasswordError.value = t('Register_Password_Mismatch')
    return false
  }

  return true
}

const sendVerificationCode = async () => {
  if (!validateEmail()) {
    return
  }

  if (turnstileEnabled && !sendCodeTurnstileToken.value) {
    showAppNotification({
      message: t('Register_Turnstile_Required'),
      type: 'warning',
      duration: 2500,
      position: 'bottom-right'
    })
    return
  }

  codeLoading.value = true
  try {
    const response = await api.auth.sendRegisterCode(email.value, 'web', sendCodeTurnstileToken.value || null)
    
    // API 直接返回 JSONResponse，格式为 { status, data }
    if (response.data?.status === '0') {
      codeSent.value = true
      showAppNotification({
        message: t('Register_Code_Sent_Success'),
        type: 'success',
        duration: 2000,
        position: 'bottom-right'
      })
    } else {
      const errorMsg = response.data?.data?.error || t('Register_Code_Send_Failed')
      showAppNotification({
        message: errorMsg,
        type: 'error',
        duration: 2500,
        position: 'bottom-right'
      })
    }
  } catch (error) {
    // 错误已由 API 拦截器处理
    logger.error('Send code error:', error)
    sendCodeTurnstileRef.value?.reset()
    sendCodeTurnstileToken.value = ''
  } finally {
    codeLoading.value = false
  }
}

const handleRegister = async () => {
  if (!validateRegisterForm()) {
    return
  }

  if (turnstileEnabled && !registerTurnstileToken.value) {
    showAppNotification({
      message: t('Register_Turnstile_Required'),
      type: 'warning',
      duration: 2500,
      position: 'bottom-right'
    })
    return
  }

  loading.value = true
  try {
    const response = await api.auth.register({
      Email: email.value,
      Password: password.value,
      Username: username.value,
      VerificationCode: verificationCode.value,
      ReferrerCode: referrerCode.value || undefined,
      ReceivePromotionalEmails: receivePromotionalEmails.value
    }, registerTurnstileToken.value || null)
    
    // 处理响应格式：API 返回的是 { content: { status, data } }
    const responseData = response.data?.content || response.data
    
    if (responseData?.status === '0' && responseData?.data?.access_token) {
      // 保存 token
      setStorage(STORAGE_KEYS.TOKEN, responseData.data.access_token)
      
      // GTM: 注册成功事件
      trackSignUp(email.value, 'email')
      
      showAppNotification({
        message: t('Register_Success'),
        type: 'success',
        duration: 2000,
        position: 'bottom-right'
      })

      // 跳转到首页
      router.push('/')
    } else {
      const errorMsg = responseData?.data?.error || t('Register_Failed')
      showAppNotification({
        message: errorMsg,
        type: 'error',
        duration: 2500,
        position: 'bottom-right'
      })
    }
  } catch (error) {
    // 错误已由 API 拦截器处理
    logger.error('Register error:', error)
    registerTurnstileRef.value?.reset()
    registerTurnstileToken.value = ''
    registerTurnstilePending.value = true
  } finally {
    loading.value = false
  }
}

const goToLogin = () => {
  router.push('/login')
}
</script>

<style scoped>
/* 确保复选框选中时使用 brand 颜色 */
input[type="checkbox"] {
  accent-color: #8B5CF6 !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border: 2px solid #d1d5db;
  border-radius: 3px;
  background-color: white;
  cursor: pointer;
  position: relative;
}

input[type="checkbox"]:checked {
  background-color: #8B5CF6 !important;
  border-color: #8B5CF6 !important;
}

input[type="checkbox"]:checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

input[type="checkbox"]:checked:focus {
  border-color: #8B5CF6 !important;
  box-shadow: 0 0 0 1px #8B5CF6 !important;
}

input[type="checkbox"]:focus {
  outline: none;
  box-shadow: 0 0 0 1px #8B5CF6;
}
</style>
